import React, { useState } from 'react';
import PropTypes from 'prop-types';

const BlogGrid = ({ blogs }) => {
  const [expandedBlogs, setExpandedBlogs] = useState(new Set());

  const toggleExpanded = (blogId) => {
    setExpandedBlogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blogId)) {
        newSet.delete(blogId);
      } else {
        newSet.add(blogId);
      }
      return newSet;
    });
  };

  // Extended content for each blog
  const getExtendedContent = (blog) => {
    const title = blog.title.toLowerCase();
    const tags = blog.tags.map(tag => tag.toLowerCase());
    
    // Health and wellness related blogs
    if (title.includes('health benefits') || title.includes('natural elixir') || tags.includes('honey benefits')) {
      return `Honey has been used for centuries as both a food and medicine. The natural sugars in honey provide quick energy, while its antibacterial properties help fight infections. Elixir's premium honey varieties are particularly rich in antioxidants, which help protect your cells from damage caused by free radicals.

Research has shown that regular consumption of high-quality honey can support immune function, improve digestive health, and even promote better sleep. Our Mediterranean honey varieties, harvested from pristine Tunisian landscapes, contain unique compounds that enhance these natural benefits.

The flavonoids and phenolic acids found in Elixir honey varieties work together to provide anti-inflammatory effects, making them an excellent addition to your daily wellness routine. Whether enjoyed in tea, drizzled on yogurt, or taken by the spoonful, our honey offers a natural way to support your health and well-being.`;
    }
    
    // Recipe related blogs
    if (title.includes('recipe') || title.includes('hot honey') || tags.includes('honey recipes')) {
      return `This versatile hot honey recipe combines the natural sweetness of Elixir honey with the perfect blend of spices to create a condiment that elevates any dish. The key to this recipe is using high-quality honey as the base - our Mediterranean varieties provide the perfect foundation.

To make this hot honey, you'll need:
• 1 cup Elixir honey (Thym or Eucalyptus variety works best)
• 2-3 fresh chili peppers, finely chopped
• 1 tablespoon apple cider vinegar
• 1 teaspoon smoked paprika
• 1/2 teaspoon garlic powder
• Salt to taste

Combine all ingredients in a small saucepan and heat gently over low heat for 10-15 minutes, stirring occasionally. The honey will become infused with the spicy flavors while maintaining its natural sweetness. Let it cool completely before transferring to a glass jar.

This hot honey is perfect for drizzling over pizza, adding to sandwiches, or using as a glaze for roasted vegetables. It also makes an excellent addition to salad dressings and marinades. Store in the refrigerator for up to 2 weeks.`;
    }
    
    // Beekeeping and educational content
    if (title.includes('beekeeping') || title.includes('guide') || title.includes('journey') || tags.includes('beekeeping')) {
      return `Beekeeping is both an art and a science, requiring patience, knowledge, and a deep respect for these remarkable insects. The journey from nectar to honey is a fascinating process that begins with the foraging bees collecting nectar from flowers.

When a bee finds a good source of nectar, it uses its proboscis to suck up the liquid and stores it in its honey stomach. Back at the hive, the forager bee passes the nectar to house bees, who process it by adding enzymes and reducing the water content through evaporation.

The bees fan their wings to create airflow, helping to evaporate excess moisture from the nectar. Once the honey reaches the right consistency (about 18% water content), the bees seal it in wax cells with a thin layer of beeswax called a capping.

This entire process can take anywhere from a few days to several weeks, depending on the weather conditions and the type of flowers the bees are visiting. The result is pure, natural honey that contains all the beneficial compounds from the original nectar source.

At Elixir, we work closely with traditional beekeepers in Tunisia who follow sustainable practices that ensure the health of both the bees and the environment. This commitment to quality and tradition is what makes our honey truly special.`;
    }
    
    // Product specific blogs (Balm of Gilead, Sweet Dreams)
    if (title.includes('balm of gilead') || title.includes('sacred trees') || title.includes('sweet dreams')) {
      return `Our Balm of Gilead Elixir represents the pinnacle of traditional honey craftsmanship, drawing upon ancient knowledge and sacred botanical sources. This unique variety is created through a specialized process that combines the finest honey with carefully selected resins and extracts.

The sacred trees mentioned in our process include the storax tree (Styrax officinalis), whose resin has been used for centuries in traditional medicine. When combined with our premium honey, these resins create a product that is both delicious and beneficial for health.

The immortelle flower (Helichrysum italicum), also known as the everlasting flower, adds its unique properties to the blend. This flower is renowned for its ability to promote healing and reduce inflammation, making it a perfect complement to the natural benefits of honey.

For those struggling with sleep, our Sweet Dreams variety is specifically formulated to promote relaxation and restful sleep. The natural compounds in this honey variety work with your body's natural sleep cycles to help you fall asleep faster and stay asleep longer.

The combination of natural sugars and specific amino acids in our honey varieties helps the brain produce serotonin, which is then converted to melatonin - the hormone responsible for regulating sleep. This natural approach to sleep support is both effective and gentle on your system.`;
    }
    
    // Default extended content
    return `Elixir honey represents the perfect harmony between traditional beekeeping practices and modern quality standards. Each variety in our collection tells a story of the Mediterranean landscape, from the wild thyme fields to the fragrant eucalyptus forests.

Our commitment to quality begins with the selection of apiary locations, chosen for their pristine environment and diverse floral sources. The beekeepers we work with follow sustainable practices that ensure the health of both the bees and the surrounding ecosystem.

The harvesting process is carefully timed to ensure that each variety captures the essence of its source flowers at their peak. This attention to detail results in honey that is not only delicious but also rich in the beneficial compounds that make each variety unique.

From the robust and aromatic Thym Elixir to the delicate and floral Eucalyptus variety, each product in our collection offers a unique taste of the Mediterranean. These varieties are more than just honey - they are a celebration of tradition, terroir, and the natural beauty of Tunisia's landscapes.

Whether you're using our honey for its health benefits, its culinary versatility, or simply to enjoy its exceptional taste, you're experiencing a product that has been crafted with care and respect for both tradition and quality.`;
  };

  return (
    <section className="py-10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <div
              key={blog.id}
              className="bg-white border border-gray-200 shadow-md rounded-lg flex flex-col hover:shadow-lg transition-shadow duration-300"
            >
              {/* Image Placeholder */}
              <div className="bg-gray-300 h-48 flex items-center justify-center rounded-t-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 7l1.664-1.664A2 2 0 016.343 5h11.314a2 2 0 011.414.586L21 7m-9 4a4 4 0 110-8 4 4 0 010 8z"
                  />
                </svg>
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-grow">
                <h3 className="font-semibold text-lg mb-1">{blog.title}</h3>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Date Published:</strong> {blog.date}
                </p>
                <p className="text-sm text-gray-600 mb-3">
                  <strong>Categories/Tags:</strong> {blog.tags.join(', ')}
                </p>
                <p className="text-sm text-gray-700 flex-grow">{blog.description}</p>
                
                {/* Expanded content */}
                {expandedBlogs.has(blog.id) && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {getExtendedContent(blog)}
                    </p>
                  </div>
                )}

                <div className="mt-4">
                  <button 
                    onClick={() => toggleExpanded(blog.id)}
                    className="text-sm border px-5 py-1.5 rounded-full transition duration-300 cursor-pointer"
                    style={{
                      borderColor: '#31492e',
                      color: '#31492e'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#31492e';
                      e.target.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#31492e';
                    }}
                  >
                    {expandedBlogs.has(blog.id) ? 'Read Less' : 'Read More'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

BlogGrid.propTypes = {
  blogs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      tags: PropTypes.arrayOf(PropTypes.string).isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default BlogGrid;
